/**
 * Core deposit — pay-upfront multi-item cart add.
 * Intercepts ATC click, builds clean JSON payload, POSTs host + core
 * to /cart/add.js in one call so the pair can never be split.
 */
(() => {
const LOG = '[CoreSellingPlan]';

// Fire-and-forget client error reporting → app proxy at /apps/dpp/api/error-report.
// WHY the /apps/dpp prefix: the Remix route only resolves on the storefront via the app
// proxy; a bare /api/error-report hits the Shopify storefront and 404s (beacon lost silently).
// Double-guarded: must NEVER throw or block the add-to-cart flow. `component` is an
// allowlisted context key on the endpoint, so reports are identifiable in Sentry as core-selling-plan.
function reportError(phase, error) {
  try {
    fetch('/apps/dpp/api/error-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'core-selling-plan',
        message: `${LOG} ${error?.message || String(error)}`,
        stack: error?.stack,
        context: { component: 'core-selling-plan', phase, url: location.pathname },
      }),
    }).catch(() => {});
  } catch {}
}

class CoreSellingPlan extends HTMLElement {
  connectedCallback() {
    this.coreVariantId = this.dataset.coreVariantId;

    const sectionEl = this.closest(`#shopify-section-${this.dataset.sectionId}`) || this.closest('.shopify-section');
    if (sectionEl) {
      this.productForm = sectionEl.querySelector('form[action="/cart/add"]') || sectionEl.querySelector('form[action*="/cart/add"]');
    }
    if (!this.productForm) return;
    if (this.productForm.__coreSellingPlan) return;
    this.productForm.__coreSellingPlan = this;

    this.cartAction = this.productForm.closest('cart-action');

    // A variant change rewrites innerHTML of the section's .jsVariantUpdate nodes, replacing
    // both the [name=id] control and the object-product JSON, so a listener on either node goes
    // dead after the first switch. Watch the section subtree instead, batched per task
    // (not requestAnimationFrame, which never runs while the tab is hidden).
    const variantScope = this.closest('.shopify-section');
    if (variantScope) {
      this._variantObserver = new MutationObserver(() => {
        if (this._syncFrame) return;
        this._syncFrame = setTimeout(() => {
          this._syncFrame = null;
          this.syncVariant();
        }, 0);
      });
      this._variantObserver.observe(variantScope, { childList: true, subtree: true });
    }

    this._atcInterceptor = (e) => {
      const btn = e.target.closest('button[type="submit"], .add-to-cart, [data-add-to-cart]');
      if (!btn) return;
      // Extend warranty also intercepts ATC (capture on button, fires after us).
      // On user clicks: let Extend handle first — it stops propagation itself.
      // Extend's done callback re-clicks programmatically (isTrusted=false) — we catch that.
      const sectionEl = this.closest('.shopify-section');
      if (e.isTrusted && sectionEl?.querySelector('.extend-offer')) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this._addToCart();
    };

    const interceptTarget = this.cartAction || this.productForm;
    interceptTarget.addEventListener('click', this._atcInterceptor, true);
  }

  async _addToCart() {
    let planData;
    try {
      planData = JSON.parse(this.dataset.corePlan);
    } catch (e) {
      console.error(LOG, 'Failed to parse plan data:', this.dataset.corePlan, e);
      reportError('plan-parse', e);
      return;
    }

    const hostId = this.productForm.querySelector('[name="id"]');
    const hostQty = this.productForm.querySelector('input[name="quantity"]');
    if (!hostId?.value) {
      console.error(LOG, 'No host variant ID found');
      reportError('no-host-id', new Error('No host variant ID found'));
      return;
    }

    const quantity = parseInt(hostQty?.value || '1', 10);

    // Match normal ATC validation: the capture-phase intercept bypasses the native form
    // validation cart-action.handleSubmit runs, so required fields (VIN, required option-app
    // inputs) could otherwise post empty. Block and surface the browser validation UI.
    if (!this.productForm.checkValidity()) {
      this.productForm.reportValidity();
      return;
    }

    const hostProps = { ...planData.host_properties };
    // WHY: collect via FormData, not productForm.querySelectorAll. Add-on/option apps
    // (theme add-ons `properties[_addOns]`, Qikify/TEPO) render their inputs OUTSIDE the
    // <form>, associated only by the form= attribute — invisible to querySelectorAll but
    // included by FormData(form). Matches the normal ATC payload so the downstream checkout
    // expander sees _addOns; without this the interceptor silently drops every add-on.
    for (const [name, value] of new FormData(this.productForm).entries()) {
      // Skip File entries (file-upload option apps) — payload is JSON, not multipart.
      if (typeof value !== 'string') continue;
      // Match the key up to the first ']' so array-notation names (properties[Opt][],
      // Qikify/TEPO multi-selects) yield the real key ("Opt"), not a mangled "Opt][".
      const match = name.match(/^properties\[([^\]]+)\]/);
      if (match) hostProps[match[1]] = value;
    }
    // Re-assert the plan's authoritative host_properties AFTER the sweep so a stray
    // form-associated properties[_parent_core_charge] (the legacy pdp-core-charge block, still
    // in-repo) can't overwrite the value the cart matcher + downstream consumers depend on.
    Object.assign(hostProps, planData.host_properties);

    // Build the host<->core pairing key from EVERYTHING that determines the host line's
    // identity: base (coreVariant+option) + host variant id + a hash of the swept properties.
    // WHY the prop hash: the FormData sweep above now carries add-on/option props on the host,
    // so the SAME variant added twice with DIFFERENT add-ons splits into two host lines
    // (Shopify keys lines on variant+properties+selling_plan) while the core lines — which
    // don't carry those props — would otherwise MERGE into one. Two hosts sharing one core
    // re-opens the stranded/orphan-core bug this PR exists to close (remove one host ->
    // shared core zeroed -> the other host ships with no core charge) plus a qty desync.
    // Hashing the props into the key makes the CORE split alongside the host; an identical
    // variant+props re-add keeps the same key so host and core still merge and qty-stack.
    const coreProps = { ...planData.core.properties };
    const basePair = planData.host_properties?._core_pair || planData.core.properties?._core_pair;
    if (basePair) {
      const fingerprint = Object.keys(hostProps)
        .filter((k) => k !== '_core_pair')
        .sort()
        .map((k) => `${k}=${hostProps[k]}`)
        .join('&');
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        hash = (Math.imul(31, hash) + fingerprint.charCodeAt(i)) | 0;
      }
      const pairKey = `${basePair}-${hostId.value}-${(hash >>> 0).toString(36)}`;
      hostProps._core_pair = pairKey;
      coreProps._core_pair = pairKey;
    }

    const body = {
      items: [
        {
          id: planData.core.id,
          quantity: quantity,
          properties: coreProps,
        },
        {
          id: parseInt(hostId.value, 10),
          quantity: quantity,
          properties: hostProps,
        },
      ],
      sections: 'cart-section,cart-live-region-text',
    };

    if (this.cartAction) this.cartAction.classList.add('loading');

    try {
      const response = await fetch(
        `${window.theme?.routes?.cartUrl || '/cart'}/add.js`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error(LOG, 'Cart add failed:', error);
        reportError('cart-add', new Error(`cart add ${response.status}: ${error?.description || error?.message || 'unknown'}`));
        if (this.cartAction) this.cartAction.classList.remove('loading');
        return;
      }

      const results = await response.json();

      if (results.sections && window.theme?.debounceEventDispatcher) {
        // The .jsCartCount header/drawer badge renders via CSS content:attr(data-count)
        // and is ONLY set by cart.js updateCartCounter, which runs inside cart.js's own
        // fetch flow. This core ATC does its own /cart/add.js + dispatches cart:update
        // directly, so it bypasses updateCartCounter and the badge stays at the page-load
        // count (0 on an empty cart) — TW #48354064. Mirror updateCartCounter here.
        // WHY the live-region value, not results.items.reduce(): /cart/add.js returns only
        // the just-added items, so reduce() undercounts when the cart already had lines.
        let cartCount;
        const liveRegion = results.sections['cart-live-region-text'];
        if (typeof liveRegion === 'string' && liveRegion.includes('<!-- split -->')) {
          cartCount = liveRegion.split('<!-- split -->')[1];
          document.querySelectorAll('.jsCartCount').forEach((el) => {
            el.setAttribute('data-count', cartCount);
          });
        } else {
          cartCount = results.items?.reduce((sum, item) => sum + item.quantity, 0);
        }
        window.theme.debounceEventDispatcher.dispatch('cart:update', {
          sections: results.sections,
          cartCount,
        });
      }

      const variantId = hostId.value;
      const algoliaQueryId = new URLSearchParams(window.location.search).get('algolia_query_id');
      if (algoliaQueryId && variantId && window.aa) {
        const eventNamesMap = { '/products/': 'Added To Cart From PDP', '/collections/': 'Added To Cart From Collection', '/search': 'Added To Cart From Search' };
        const eventName = eventNamesMap[Object.keys(eventNamesMap).find(s => window.location.pathname.includes(s))] ?? 'Added To Cart';
        window.aa('addedToCartObjectIDsAfterSearch', {
          eventName,
          index: 'shopify_products_manual_index_prod',
          queryID: algoliaQueryId,
          objectIDs: [`${this.cartAction?.dataset?.productId || ''}_${variantId}`],
        });
      }
      if (variantId) {
        window.dispatchEvent(new CustomEvent('algolia:atc', {
          detail: { variantId, quantity, eventName: 'Added To Cart' },
        }));
      }
    } catch (e) {
      console.error(LOG, 'Cart add error:', e);
      reportError('cart-add-exception', e);
    } finally {
      if (this.cartAction) {
        this.cartAction.classList.remove('loading');
        if (this.cartAction.getAttribute('data-once') === 'true') {
          this.cartAction.classList.add('added');
        }
      }
    }
  }

  // Keeps the core line's SKU label and the three displayed prices on the selected host variant.
  syncVariant() {
    const sectionEl = this.closest('.shopify-section');
    if (!sectionEl) return;
    const jsonScript = sectionEl.querySelector('script[type="application/json"].object-product');
    if (!jsonScript) return;
    let variant;
    try {
      variant = JSON.parse(jsonScript.textContent).current_variant;
    } catch (e) {
      return;
    }
    if (!variant) return;

    if (variant.sku) {
      try {
        const data = JSON.parse(this.dataset.corePlan);
        if (data.core.properties['Core Charge for Product'] !== variant.sku) {
          data.core.properties['Core Charge for Product'] = variant.sku;
          this.dataset.corePlan = JSON.stringify(data);
        }
      } catch (e) {}
    }

    const hostPrice = Number(variant.price);
    const corePrice = Number(this.dataset.corePrice);
    if (!Number.isFinite(hostPrice) || !Number.isFinite(corePrice)) return;
    const hostEl = this.querySelector('.jsCoreHostPrice');
    const totalEl = this.querySelector('.jsCoreTotalPrice');
    const hostText = this.formatCents(hostPrice);
    const totalText = this.formatCents(hostPrice + corePrice);
    // Write only on change: these writes are themselves mutations inside the observed subtree.
    if (hostEl && hostEl.textContent !== hostText) hostEl.textContent = hostText;
    if (totalEl && totalEl.textContent !== totalText) totalEl.textContent = totalText;
  }

  formatCents(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
  }

  disconnectedCallback() {
    if (this._variantObserver) this._variantObserver.disconnect();
    // Null the id too: a stale one survives a detach/reattach and makes the observer skip forever.
    if (this._syncFrame) clearTimeout(this._syncFrame);
    this._syncFrame = null;
    if (this.productForm) {
      if (this.productForm.__coreSellingPlan === this) delete this.productForm.__coreSellingPlan;
    }
    const interceptTarget = this.cartAction || this.productForm;
    if (interceptTarget && this._atcInterceptor) {
      interceptTarget.removeEventListener('click', this._atcInterceptor, true);
    }
  }
}

if (!customElements.get('core-selling-plan')) {
  customElements.define('core-selling-plan', CoreSellingPlan);
}
})();
